/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./content.css";
import { useNavigate } from "react-router";
import { resolveUrl } from "../../../utils/storeUtils";
import { validateAboutUsBackup } from "../../../utils/aboutUsBundle";

const EMPTY_IMAGE_SLOTS = () => ({
  one: { file: null, preview: null, alt: "" },
  two: { file: null, preview: null, alt: "" },
  three: { file: null, preview: null, alt: "" },
  four: { file: null, preview: null, alt: "" },
});

const About = () => {
  const [mainText, setMainText] = useState("");
  const [arMainText, setArMainText] = useState("");
  const [images, setImages] = useState({
    one: { file: null, preview: null, alt: "" },
    two: { file: null, preview: null, alt: "" },
    three: { file: null, preview: null, alt: "" },
    four: { file: null, preview: null, alt: "" },
  });
  const [aboutData, setAboutData] = useState(null);
  const [value, setValue] = useState("");
  const [arValue, setArValue] = useState("");
  const [valueImage, setValueImage] = useState(null);
  const [valueImageAlt, setValueImageAlt] = useState("");
  const [valueImageArAlt, setValueImageArAlt] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [values, setValues] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedValueId, setSelectedValueId] = useState(null);
  const [questionTitle, setQuestionTitle] = useState("");
  const [arQuestionTitle, setArQuestionTitle] = useState("");
  const [questionDescription, setQuestionDescription] = useState("");
  const [arQuestionDescription, setArQuestionDescription] = useState("");
  const [questions, setQuestions] = useState([]);
  const [boxesToShow, setBoxesToShow] = useState(7);
  const [questionsToShow, setQuestionsToShow] = useState(3);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [applyImportLoading, setApplyImportLoading] = useState(false);
  /** Validated JSON from file; applied to form in preview mode until "Apply Imported Data". */
  const [pendingImportPayload, setPendingImportPayload] = useState(null);
  /** Advanced: upload JSON writes to DB immediately (old behavior). */
  const [directImportOnUpload, setDirectImportOnUpload] = useState(false);
  const importFileRef = useRef(null);
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
  const apiBaseUrl = axios.defaults.baseURL || "";

  const navigate = useNavigate();

  const applyAboutToForm = useCallback(
    (data) => {
      if (!data) {
        setMainText("");
        setArMainText("");
        setImages(EMPTY_IMAGE_SLOTS());
        return;
      }
      setMainText(data.mainText || "");
      setArMainText(data.arMainText || "");
      setImages({
        one: {
          file: null,
          preview: data.images?.[0]?.url
            ? resolveUrl(data.images[0].url, apiBaseUrl)
            : null,
          alt: data.images?.[0]?.alt || "",
        },
        two: {
          file: null,
          preview: data.images?.[1]?.url
            ? resolveUrl(data.images[1].url, apiBaseUrl)
            : null,
          alt: data.images?.[1]?.alt || "",
        },
        three: {
          file: null,
          preview: data.images?.[2]?.url
            ? resolveUrl(data.images[2].url, apiBaseUrl)
            : null,
          alt: data.images?.[2]?.alt || "",
        },
        four: {
          file: null,
          preview: data.images?.[3]?.url
            ? resolveUrl(data.images[3].url, apiBaseUrl)
            : null,
          alt: data.images?.[3]?.alt || "",
        },
      });
    },
    [apiBaseUrl]
  );

  const loadAllAboutPageData = useCallback(async () => {
    try {
      let aboutPayload = null;
      try {
        const r = await axios.get("/content/about");
        aboutPayload = r.data;
      } catch (e) {
        if (e.response?.status !== 404) throw e;
      }
      setAboutData(aboutPayload);
      applyAboutToForm(aboutPayload);
      const [vr, qr] = await Promise.all([
        axios.get("/content/values"),
        axios.get("/content/questions"),
      ]);
      setValues(vr.data || []);
      setQuestions(qr.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load About Us data.");
    }
  }, [applyAboutToForm]);

  const stabilizeListForPreview = (items, keyPrefix) => {
    if (!Array.isArray(items)) return [];
    return items.map((item, i) => ({
      ...item,
      _id:
        item && item._id != null && String(item._id).trim() !== ""
          ? String(item._id)
          : `${keyPrefix}-preview-${i}`,
    }));
  };

  const applyBundlePreviewToForm = useCallback(
    (data) => {
      if ("about" in data) {
        applyAboutToForm(data.about === null ? null : data.about);
      }
      if ("values" in data) {
        setValues(stabilizeListForPreview(data.values, "value"));
      }
      if ("questions" in data) {
        setQuestions(stabilizeListForPreview(data.questions, "question"));
      }
    },
    [applyAboutToForm]
  );

  /** Build POST body from staged keys + current form (text, alts, lists). */
  const buildImportApplyBody = useCallback(() => {
    if (!pendingImportPayload) return null;
    const p = pendingImportPayload;
    const body = {};
    const slotKeys = ["one", "two", "three", "four"];

    if ("about" in p) {
      if (p.about === null) {
        body.about = null;
      } else if (p.about && typeof p.about === "object") {
        const srcImages = Array.isArray(p.about.images) ? p.about.images : [];
        const mergedImages = srcImages.map((img, i) => {
          if (!img || typeof img !== "object" || !img.url) return img;
          const slot = images[slotKeys[i]];
          return {
            url: img.url,
            alt: slot?.alt != null && slot.alt !== "" ? slot.alt : img.alt || "",
            arAlt: img.arAlt || "",
          };
        });
        body.about = {
          ...p.about,
          mainText,
          arMainText,
          images: mergedImages,
        };
      }
    }
    if ("values" in p) body.values = values;
    if ("questions" in p) body.questions = questions;
    return body;
  }, [pendingImportPayload, mainText, arMainText, images, values, questions]);

  const handleDiscardStagedImport = async () => {
    setPendingImportPayload(null);
    await loadAllAboutPageData();
    toast.info("Preview discarded. Restored data from the server.");
  };

  const handleApplyImportedData = async () => {
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    if (!pendingImportPayload) {
      toast.warn("Load a JSON file first.");
      return;
    }
    const body = buildImportApplyBody();
    if (!body || Object.keys(body).length === 0) {
      toast.error("Nothing to apply.");
      return;
    }
    try {
      setApplyImportLoading(true);
      await axios.post("/content/about-page/import", body, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setPendingImportPayload(null);
      toast.success("Imported data saved to the database.");
      await loadAllAboutPageData();
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Could not apply imported data."
      );
    } finally {
      setApplyImportLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  const handleFileUpload = (file, key) => {
    const preview = URL.createObjectURL(file);
    setImages((prevImages) => ({
      ...prevImages,
      [key]: { file, preview, alt: prevImages[key].alt },
    }));
  };

  const handleAltChange = (altText, key) => {
    setImages((prevImages) => ({
      ...prevImages,
      [key]: { ...prevImages[key], alt: altText },
    }));
  };

  const handleImageDelete = (key) => {
    setImages((prevImages) => ({
      ...prevImages,
      [key]: { file: null, preview: null, alt: "" },
    }));
  };

  const handleSaveSectionOne = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("mainText", mainText);
    formData.append("arMainText", arMainText);

    const imagesData = [
      { file: images.one.file, alt: images.one.alt, key: "images[0]" },
      { file: images.two.file, alt: images.two.alt, key: "images[1]" },
      { file: images.three.file, alt: images.three.alt, key: "images[2]" },
      { file: images.four.file, alt: images.four.alt, key: "images[3]" },
    ];

    imagesData.forEach((image) => {
      if (image.file) {
        formData.append(image.key, image.file);
        formData.append(`${image.key}_alt`, image.alt);
      }
    });

    toast
      .promise(
        axios.post("/content/about", formData, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        }),
        {
          pending: "Uploading data...",
          success: "Section saved successfully!",
          error: "Error uploading data.",
        }
      )
      .then(() => loadAllAboutPageData())
      .catch(() => {});
  };

  const handleAddValue = async (e) => {
    e.preventDefault();

    if (
      !value ||
      !arValue ||
      !valueImage ||
      !valueImageAlt ||
      !valueImageArAlt
    ) {
      return toast.error("Please fill out all fields.");
    }

    const valueData = new FormData();
    valueData.append("value", value);
    valueData.append("arValue", arValue);
    valueData.append("valueImage", valueImage);
    valueData.append("alt", valueImageAlt);
    valueData.append("arAlt", valueImageArAlt);

    try {
      const response = await toast.promise(
        axios.post("/content/values", valueData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }),
        {
          pending: "Adding value...",
          success: "Value added successfully!",
        }
      );
      setValue("");
      setArValue("");
      setValueImage(null);
      setValueImageAlt("");
      setValueImageArAlt("");
      await loadAllAboutPageData();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteConfirm = (valueId) => {
    setSelectedValueId(valueId);
    setShowConfirmation(true);
  };

  const handleDeleteValue = async () => {
    try {
      await axios.delete(`/content/values/${selectedValueId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setValues(values.filter((value) => value._id !== selectedValueId));
      toast.success("Value has been deleted..!");
      setShowConfirmation(false);
      setSelectedValueId(null);
    } catch (error) {
      console.error("Error deleting value:", error);
      toast.error(
        error.response.data.message || "An error occurred while deleting."
      );
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();

    if (
      !questionTitle ||
      !arQuestionTitle ||
      !questionDescription ||
      !arQuestionDescription
    ) {
      return toast.error("Please fill out all fields.");
    }

    const questionData = {
      title: questionTitle,
      arTitle: arQuestionTitle,
      description: questionDescription,
      arDescription: arQuestionDescription,
    };

    try {
      const response = await toast.promise(
        axios.post("/content/questions", questionData, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        {
          pending: "Adding question...",
          success: "Question added successfully!",
        }
      );
      setQuestionTitle("");
      setArQuestionTitle("");
      setQuestionDescription("");
      setArQuestionDescription("");
      await loadAllAboutPageData();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      await axios.delete(`/content/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuestions(questions.filter((question) => question._id !== questionId));
      toast.success("Question has been deleted..!");
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error(
        error.response.data.message || "An error occurred while deleting."
      );
    }
  };

  const handleShowPopup = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleToggleShowBoxes = () => {
    if (boxesToShow > 7) {
      setBoxesToShow(7);
    } else if (boxesToShow === 7) {
      setBoxesToShow(values?.length);
    }
  };

  const handleToggleShowQuestions = () => {
    if (questionsToShow > 3) {
      setQuestionsToShow(3);
    } else if (questionsToShow === 3) {
      setQuestionsToShow(questions?.length);
    }
  };

  const handleDownloadAboutUsBundle = async () => {
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      setExportLoading(true);
      const res = await axios.get("/content/about-page/bundle", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = JSON.stringify(res.data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `about-us-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("About Us backup downloaded.");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Could not export About Us data."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    const nameOk = /\.json$/i.test(file.name);
    const typeOk =
      file.type === "application/json" || file.type === "text/json" || !file.type;
    if (!nameOk && !typeOk) {
      toast.error("Please choose a .json file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setImportLoading(true);
        let data;
        try {
          data = JSON.parse(String(reader.result || ""));
        } catch {
          toast.error("Invalid JSON. The file could not be parsed.");
          return;
        }
        const check = validateAboutUsBackup(data);
        if (!check.ok) {
          toast.error(check.message);
          return;
        }

        const snapshot = JSON.parse(JSON.stringify(data));

        if (directImportOnUpload) {
          await axios.post("/content/about-page/import", data, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          setPendingImportPayload(null);
          toast.success("About Us data saved to the database.");
          await loadAllAboutPageData();
        } else {
          setPendingImportPayload(snapshot);
          applyBundlePreviewToForm(data);
          toast.success(
            "Import file loaded into the form. Review, then click Apply Imported Data."
          );
        }
      } catch (error) {
        console.error(error);
        toast.error(
          error.response?.data?.message || "Import failed. Check the file format."
        );
      } finally {
        setImportLoading(false);
      }
    };
    reader.onerror = () => {
      toast.error("Could not read the file.");
      setImportLoading(false);
    };
    reader.readAsText(file, "UTF-8");
  };

  useEffect(() => {
    loadAllAboutPageData();
  }, [loadAllAboutPageData]);

  return (
    <div className="dash-content about about-page">
      <div className="container">
        <form onSubmit={handleSaveSectionOne}>
          <div className="section one about-page__card">
            <h1 className="dash-title">About Us</h1>
            <p className="about-page__lead">
              Edit the story visitors see on the About page. Save when you are done
              with copy and gallery images.
            </p>
            <div className="input">
              <h2>Main Text</h2>
              <textarea
                placeholder={aboutData?.mainText}
                value={mainText}
                onChange={(e) => setMainText(e.target.value)}
              ></textarea>
            </div>
            <div className="input ar-input">
              <h2>النص الرئيسي</h2>
              <textarea
                placeholder={aboutData?.arMainText || "اكتب النص الرئيسي"}
                value={arMainText}
                onChange={(e) => setArMainText(e.target.value)}
              ></textarea>
            </div>
            <hr className="about-page__divider about-page__divider--inset" />
          </div>
          <div className="section two about-page__card">
            <div className="about-images">
              <h2>Images</h2>
              <p className="about-page__section-hint">
                Up to four images. Alt text improves accessibility and SEO.
              </p>
              <div className="images">
                {Object.keys(images).map((key) => (
                  <div className="image-container" key={key}>
                    <div className="image">
                      {images[key].preview ? (
                        <div className="image-preview">
                          <img src={images[key].preview} alt="Preview" />
                          <button
                            type="button"
                            className="delete-btn"
                            onClick={() => handleImageDelete(key)}
                          >
                            <i className="uil uil-trash-alt"></i>
                          </button>
                        </div>
                      ) : (
                        <>
                          <i className="uil uil-image-v"></i>
                          <p>Drop your image here, or</p>
                          <div className="btn">
                            <button type="button">Click here</button>
                            <input
                              type="file"
                              onChange={(e) =>
                                handleFileUpload(e.target.files[0], key)
                              }
                              accept="image/*"
                            />
                          </div>
                        </>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="Alt text"
                      value={images[key].alt}
                      onChange={(e) => handleAltChange(e.target.value, key)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="about-page__actions">
            <button type="submit" className="submit">
              Save About section
            </button>
          </div>
        </form>
        <hr className="about-page__divider" />
        <div className="section three about-page__card">
          <h2>Things we value</h2>
          <p className="about-page__section-hint">
            Short value statements with an icon or photo each.
          </p>
          <div className="values">
            <div className="boxes">
              <div className="add-value" onClick={handleShowPopup}>
                <i className="uil uil-plus"></i>
              </div>
              {values?.slice(0, boxesToShow)?.map((value) => (
                <div className="box" key={value?._id}>
                  <button className="remove-btn">
                    <i
                      className="uil uil-trash-alt"
                      onClick={() => handleDeleteConfirm(value?._id)}
                    ></i>
                  </button>
                  <div className="image">
                    <img src={value?.image} alt={value?.alt} />
                  </div>
                  <h3>{value?.value}</h3>
                </div>
              ))}
            </div>

            {showPopup === true && (
              <div className="value-popup">
                <form className="popup-content" onSubmit={handleAddValue}>
                  <div className="head">
                    <h2>Value</h2>
                    <i className="uil uil-times" onClick={handleClosePopup}></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Value"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                  <input
                    className="ar-input"
                    type="text"
                    placeholder="القيمة"
                    value={arValue}
                    onChange={(e) => setArValue(e.target.value)}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setValueImage(e.target.files[0])}
                  />
                  <input
                    type="text"
                    placeholder="Alt"
                    value={valueImageAlt}
                    onChange={(e) => setValueImageAlt(e.target.value)}
                  />
                  <input
                    className="ar-input"
                    type="text"
                    placeholder="النص البديل"
                    value={valueImageArAlt}
                    onChange={(e) => setValueImageArAlt(e.target.value)}
                  />
                  <button>Add</button>
                </form>
              </div>
            )}

            {values?.length > 7 && (
              <p className="show-more" onClick={handleToggleShowBoxes}>
                Show {boxesToShow === 7 ? "more" : "less"}
              </p>
            )}

            {showConfirmation && (
              <div className="confirmation-modal">
                <div className="modal-content">
                  <img src="/photos/question.svg" alt="?" />
                  <p>Are you sure you want to delete this value?</p>
                  <div className="modal-actions">
                    <button onClick={handleDeleteValue}>Yes</button>
                    <button onClick={() => setShowConfirmation(false)}>
                      No
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <hr className="about-page__divider" />
        <div className="section four about-page__card">
          <h2>What makes us different</h2>
          <p className="about-page__section-hint">
            FAQ-style blocks: a title and short description (English and Arabic).
          </p>
          <form className="add-question" onSubmit={handleAddQuestion}>
            <div className="inputs-container">
              <div className="inputs">
                <input
                  type="text"
                  placeholder="Title."
                  value={questionTitle}
                  onChange={(e) => setQuestionTitle(e.target.value)}
                />
                <input
                  className="ar-input"
                  type="text"
                  placeholder="العنوان."
                  value={arQuestionTitle}
                  onChange={(e) => setArQuestionTitle(e.target.value)}
                />
              </div>
              <div className="inputs">
                <input
                  type="text"
                  placeholder="Description."
                  value={questionDescription}
                  onChange={(e) => setQuestionDescription(e.target.value)}
                />
                <input
                  className="ar-input"
                  type="text"
                  placeholder="الوصف."
                  value={arQuestionDescription}
                  onChange={(e) => setArQuestionDescription(e.target.value)}
                />
              </div>
            </div>
            <div className="about-page__actions about-page__actions--inline">
              <button type="submit" className="submit">
                Add question
              </button>
            </div>
          </form>
          <div className="questions">
            {questions?.slice(0, questionsToShow)?.map((question) => (
              <div className="question" key={question?._id}>
                <div className="head">
                  <h3>{question?.title}</h3>
                  <button className="remove-btn">
                    <i
                      className="uil uil-trash-alt"
                      onClick={() => handleDeleteQuestion(question?._id)}
                    ></i>
                  </button>
                </div>
                <p>{question?.description}</p>
              </div>
            ))}
          </div>
          {questions?.length > 3 && (
            <p className="show-more" onClick={handleToggleShowQuestions}>
              Show {questionsToShow === 3 ? "more" : "less"}
            </p>
          )}
        </div>

        <section
          className="about-us-backup-panel"
          aria-label="Backup and restore About Us"
        >
          <div className="about-us-backup-panel__head">
            <div className="about-us-backup-panel__head-icon" aria-hidden="true">
              <i className="uil uil-database-alt" />
            </div>
            <div className="about-us-backup-panel__head-text">
              <h2 className="about-us-backup-panel__title">Backup &amp; import</h2>
              <p className="about-us-backup-panel__subtitle">
                Export a JSON snapshot or load one into this page. Default flow:
                load → review above → apply.
              </p>
            </div>
          </div>

          <ol className="about-us-backup-steps" aria-label="Import workflow">
            <li>
              <span className="about-us-backup-steps__num">1</span>
              <span>
                <strong>Download</strong> a backup before risky changes.
              </span>
            </li>
            <li>
              <span className="about-us-backup-steps__num">2</span>
              <span>
                <strong>Load JSON</strong> — fills the form only (no database write).
              </span>
            </li>
            <li>
              <span className="about-us-backup-steps__num">3</span>
              <span>
                <strong>Apply imported data</strong> — saves to the server (overwrites
                included sections).
              </span>
            </li>
          </ol>

          <div className="about-us-backup-actions">
            <button
              type="button"
              className="about-us-backup-btn about-us-backup-btn--export"
              disabled={exportLoading || importLoading || applyImportLoading}
              onClick={handleDownloadAboutUsBundle}
            >
              <i className="uil uil-export" aria-hidden="true" />
              {exportLoading ? "Exporting…" : "Download JSON backup"}
            </button>
            <input
              ref={importFileRef}
              type="file"
              accept=".json,application/json,text/json"
              className="about-us-backup-file-input"
              onChange={handleImportFileSelected}
            />
            <button
              type="button"
              className="about-us-backup-btn about-us-backup-btn--import"
              disabled={exportLoading || importLoading || applyImportLoading}
              onClick={() => importFileRef.current?.click()}
            >
              <i className="uil uil-import" aria-hidden="true" />
              {importLoading
                ? "Reading file…"
                : directImportOnUpload
                  ? "Upload & save now"
                  : "Load JSON into form"}
            </button>
          </div>

          <div className="about-us-backup-advanced-row">
            <label className="about-us-backup-switch">
              <input
                type="checkbox"
                className="about-us-backup-switch__input"
                checked={directImportOnUpload}
                onChange={(e) => {
                  const on = e.target.checked;
                  setDirectImportOnUpload(on);
                  if (on) setPendingImportPayload(null);
                }}
                disabled={importLoading || applyImportLoading}
              />
              <span className="about-us-backup-switch__track" aria-hidden="true">
                <span className="about-us-backup-switch__thumb" />
              </span>
              <span className="about-us-backup-switch__label">
                Advanced: write to database immediately on upload (skip preview &amp;
                Apply)
              </span>
            </label>
          </div>

          {pendingImportPayload && !directImportOnUpload && (
            <div className="about-us-import-staged">
              <div className="about-us-import-staged__icon" aria-hidden="true">
                <i className="uil uil-exclamation-triangle" />
              </div>
              <div className="about-us-import-staged__body">
                <p className="about-us-import-warning" role="alert">
                  <strong>Preview loaded.</strong> Data is in the form only until you
                  apply.{" "}
                  <strong className="about-us-import-warning__emphasis">
                    Applying will overwrite current server data
                  </strong>{" "}
                  for About, values, and/or questions included in this file.
                </p>
                <div className="about-us-import-staged-actions">
                  <button
                    type="button"
                    className="about-us-backup-btn about-us-backup-btn--apply"
                    disabled={applyImportLoading}
                    onClick={handleApplyImportedData}
                  >
                    {applyImportLoading ? "Applying…" : "Apply imported data"}
                  </button>
                  <button
                    type="button"
                    className="about-us-backup-btn about-us-backup-btn--ghost"
                    disabled={applyImportLoading}
                    onClick={handleDiscardStagedImport}
                  >
                    Discard preview
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default About;
